/**
 * 视频时间轴 - 简化版（无 FFmpeg 剪辑功能）
 * 功能：视频预览、播放、拖动排序、删除
 */

import React, { useState, useRef } from 'react';
import {
  Play, Pause, Trash2, ChevronUp, ChevronDown,
  Film, GripVertical, X, Plus, Check, Layers
} from 'lucide-react';

interface VideoClip {
  id: string;
  videoUrl: string;
  title: string;
  createdAt: string;
  resolution: string;
}

interface VideoTimelineProps {
  tasks: any[];
  isOpen: boolean;
  toggleOpen: () => void;
  onDeleteClip?: (id: string) => void;
}

const VideoTimeline: React.FC<VideoTimelineProps> = ({
  tasks,
  isOpen,
  toggleOpen,
  onDeleteClip,
}) => {
  const [clips, setClips] = useState<VideoClip[]>([]);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [draggedClipId, setDraggedClipId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const selectedClip = clips.find(c => c.id === selectedClipId);
  
  // 过滤可导入的任务
  const availableTasksToImport = tasks.filter(task =>
    task.status === 'completed' && task.videoUrl && !clips.some(clip => clip.id === task.id)
  );
  
  // 导入片段
  const importClip = (task: any) => {
    const newClip: VideoClip = {
      id: task.id,
      videoUrl: task.videoUrl,
      title: task.prompt.slice(0, 50) + (task.prompt.length > 50 ? '...' : ''),
      createdAt: task.createdAt,
      resolution: task.resolution === 'horizontal' ? '16:9' : '9:16',
    };
    setClips(prev => [...prev, newClip]);
    setSelectedClipId(newClip.id);
    setShowImportModal(false);
  };
  
  // 播放控制
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };
  
  // 删除片段
  const deleteClip = async (id: string) => {
    if (!confirm('确定要删除这个片段吗？')) return;
    
    try {
      if (onDeleteClip) {
        await onDeleteClip(id);
      }
      setClips(prev => prev.filter(c => c.id !== id));
      if (selectedClipId === id) {
        setSelectedClipId(null);
      }
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败');
    }
  };
  
  // 拖动排序
  const handleDragStart = (id: string) => {
    setDraggedClipId(id);
  };
  
  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedClipId || draggedClipId === targetId) return;
    
    const draggedIndex = clips.findIndex(c => c.id === draggedClipId);
    const targetIndex = clips.findIndex(c => c.id === targetId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    const newClips = [...clips];
    const [draggedClip] = newClips.splice(draggedIndex, 1);
    newClips.splice(targetIndex, 0, draggedClip);
    
    setClips(newClips);
  };
  
  const handleDragEnd = () => {
    setDraggedClipId(null);
  };
  
  return (
    <div
      className={`transition-all duration-300 flex flex-col ${isOpen ? 'h-[480px]' : 'h-12'}`}
      style={{
        background: 'linear-gradient(180deg, rgba(42, 32, 21, 0.95) 0%, rgba(26, 20, 16, 0.98) 100%)',
        borderTop: '2px solid rgba(201, 165, 92, 0.3)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* 标题栏 */}
      <div
        className="h-12 flex items-center justify-between px-5 cursor-pointer"
        style={{
          background: 'linear-gradient(90deg, rgba(201, 165, 92, 0.15) 0%, transparent 100%)',
          borderBottom: isOpen ? '1px solid rgba(201, 165, 92, 0.2)' : 'none',
        }}
        onClick={toggleOpen}
      >
        <div className="flex items-center gap-4">
          <Film size={20} className="text-amber-400" />
          <span className="text-amber-200 font-bold">视频片段</span>
          <span className="text-amber-500 text-sm">{clips.length} 个</span>
        </div>
        
        <div className="flex items-center gap-2">
          {availableTasksToImport.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowImportModal(true); }}
              className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={14} /> 导入片段 ({availableTasksToImport.length})
            </button>
          )}
          
          {isOpen ? <ChevronDown size={20} className="text-amber-400" /> : <ChevronUp size={20} className="text-amber-400" />}
        </div>
      </div>
      
      {isOpen && (
        <div className="flex-1 flex overflow-hidden">
          {clips.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-amber-500/60">
              <div className="text-center">
                <Film size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-lg">暂无导入的片段</p>
                {availableTasksToImport.length > 0 && (
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="mt-4 px-6 py-3 rounded-lg text-base font-medium flex items-center gap-2 mx-auto bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus size={18} /> 导入视频片段 ({availableTasksToImport.length} 个可用)
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* 左侧：视频预览 */}
              <div className="flex-1 flex flex-col p-4 border-r border-amber-900/50">
                {selectedClip ? (
                  <>
                    <div className="flex-1 flex items-center justify-center bg-black/50 rounded-lg overflow-hidden mb-3">
                      <video
                        ref={videoRef}
                        src={selectedClip.videoUrl}
                        className="max-w-full max-h-full"
                        loop
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                      />
                    </div>
                    
                    {/* 播放控制 */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={togglePlay}
                        className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #c9a55c 0%, #967642 100%)',
                          color: '#1a1410',
                        }}
                      >
                        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                      </button>
                      
                      <div className="flex-1">
                        <p className="text-amber-200 font-medium text-sm mb-1">{selectedClip.title}</p>
                        <p className="text-amber-500 text-xs">
                          {selectedClip.resolution} · {new Date(selectedClip.createdAt).toLocaleString()}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => deleteClip(selectedClip.id)}
                        className="p-2 bg-red-900/50 hover:bg-red-700 text-red-300 rounded transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-amber-500/60">
                    <div className="text-center">
                      <Film size={40} className="mx-auto mb-2 opacity-50" />
                      <p>请选择一个片段</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* 右侧：片段列表 */}
              <div className="w-[400px] flex flex-col">
                <div className="h-10 flex items-center px-3 border-b border-amber-900/30" style={{ background: 'rgba(201, 165, 92, 0.1)' }}>
                  <Layers size={16} className="text-amber-400 mr-2" />
                  <span className="text-amber-400 text-sm font-bold">片段列表</span>
                </div>
                
                <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                  {clips.map((clip, index) => {
                    const isSelected = clip.id === selectedClipId;
                    const isDragging = clip.id === draggedClipId;
                    
                    return (
                      <div
                        key={clip.id}
                        draggable
                        onDragStart={() => handleDragStart(clip.id)}
                        onDragOver={(e) => handleDragOver(e, clip.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => setSelectedClipId(clip.id)}
                        className={`relative p-3 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-amber-700/30 border-2 border-amber-500'
                            : 'bg-amber-900/20 border border-amber-900/50 hover:bg-amber-800/30'
                        } ${isDragging ? 'opacity-50 scale-95' : ''}`}
                      >
                        <div className="absolute top-2 left-2 cursor-grab active:cursor-grabbing text-amber-500">
                          <GripVertical size={16} />
                        </div>
                        
                        <div className="absolute top-2 right-2 w-6 h-6 bg-amber-700 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        
                        <div className="ml-6 mr-8">
                          <p className="text-amber-200 font-medium text-sm mb-1 truncate">{clip.title}</p>
                          <p className="text-amber-500 text-xs">{clip.resolution}</p>
                          <p className="text-amber-600 text-xs mt-1">
                            {new Date(clip.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* 导入片段弹窗 */}
      {showImportModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-gradient-to-b from-amber-50 to-orange-50 rounded-xl shadow-2xl w-[90vw] max-w-4xl h-[80vh] flex flex-col border-4 border-amber-400 animate-in zoom-in-95 duration-300">
            <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-3 flex items-center justify-between border-b border-amber-300 z-10">
              <h3 className="text-amber-900 font-bold flex items-center gap-2">
                <Plus size={20} /> 导入视频片段 ({availableTasksToImport.length} 个可用)
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-1.5 hover:bg-amber-200 rounded-full transition-colors text-amber-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableTasksToImport.length === 0 ? (
                <div className="col-span-full text-center text-amber-700/70 py-10">
                  <p className="text-lg mb-2">暂无可用片段</p>
                  <p className="text-sm">请先生成视频任务</p>
                </div>
              ) : (
                availableTasksToImport.map(task => (
                  <div key={task.id} className="bg-white rounded-lg shadow-md border border-amber-200 overflow-hidden">
                    <div className="aspect-video bg-gray-200 flex items-center justify-center">
                      <video src={task.videoUrl} className="w-full h-full object-cover" muted />
                    </div>
                    <div className="p-3">
                      <p className="text-amber-900 font-bold text-sm mb-1 truncate">{task.prompt.slice(0, 50)}{task.prompt.length > 50 ? '...' : ''}</p>
                      <p className="text-amber-700 text-xs mb-2">创建于: {new Date(task.createdAt).toLocaleString()}</p>
                      <p className="text-amber-600 text-xs">分辨率: {task.resolution === 'horizontal' ? '16:9' : '9:16'}</p>
                      <button
                        onClick={() => importClip(task)}
                        className="mt-3 w-full py-2 bg-amber-700 hover:bg-amber-600 text-white rounded-lg text-sm flex items-center justify-center gap-1"
                      >
                        <Check size={14} /> 导入此片段
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(201, 165, 92, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(201, 165, 92, 0.4);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(201, 165, 92, 0.6);
        }
      `}</style>
    </div>
  );
};

export default VideoTimeline;
